"use client";

import { useState } from "react";
import { PlanTier } from "@cabal/shared/domain/billing/enums.js";
import { useAuthModal } from "@/contexts/layout";
import {
  TeamModalStatus,
  TeamModalSubjectKind,
  useTeamModal,
} from "@/contexts/team-modal";
import type { HomePageModalStateLocal } from "./domain/home-page/types";
import { getIsTeamGalleryCoveredByDetails } from "./homePageModalSelectors";
import { useHasOpened } from "./useHasOpened";

export function useHomePageModalState(): HomePageModalStateLocal {
  // The auth gate is the one app-wide instance, so the page reads and drives it
  // through the provider instead of owning a second copy of that state.
  const {
    openAuthModal,
    closeAuthModal,
    isAuthModalOpen: showAuthModal,
  } = useAuthModal();
  // The team modal is likewise the one app-wide instance (the canonical
  // controller): the landing DERIVES its readings from that state — the
  // "details" flags below are the controller's openness, never a local twin.
  const { state: teamModalState } = useTeamModal();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [pricingAccessLoadingPlanKey, setPricingAccessLoadingPlanKey] =
    useState<PlanTier | null>(null);
  const openTeamModalState =
    teamModalState.status === TeamModalStatus.Open ? teamModalState : null;
  const showTeamDetailsModal = openTeamModalState != null;
  // The creation subject has no id yet — the reading is honest about that.
  const openTeamModalSubject = openTeamModalState?.subject ?? null;
  const teamDetailsTeamId =
    openTeamModalSubject != null &&
    openTeamModalSubject.kind === TeamModalSubjectKind.Existing
      ? openTeamModalSubject.teamId
      : null;
  const hasOpenedPricingModal = useHasOpened(showPricingModal);
  const hasOpenedTeamsModal = useHasOpened(showTeamsModal);
  const hasOpenedTeamDetailsModal = useHasOpened(showTeamDetailsModal);
  const isTeamGalleryCoveredByDetails = getIsTeamGalleryCoveredByDetails({
    showTeamDetailsModal,
    showTeamsModal,
  });

  return {
    hasOpenedPricingModal,
    hasOpenedTeamDetailsModal,
    hasOpenedTeamsModal,
    isTeamGalleryCoveredByDetails,
    pricingAccessLoadingPlanKey,
    setPricingAccessLoadingPlanKey,
    openAuthModal,
    closeAuthModal,
    setShowPricingModal,
    setShowTeamsModal,
    showAuthModal,
    showPricingModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
  };
}
